<template>
  <div class="grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
    <!-- Left Column: Image (same style as assets: circular, border) -->
    <div class="md:col-span-3 flex flex-col items-start">
      <ProfileImage
        :src="contact.image || contact.image_url"
        alt="Contact"
        size="medium"
      />

      <!-- File input for uploading a new image, visible only when editing -->
      <FileUpload
        v-if="isEditing"
        mode="basic"
        accept="image/*"
        :auto="true"
        choose-label="Upload Image"
        :disabled="isUploadingImage"
        class="mt-4 w-full"
        @select="onImageUpload"
      />
      <div
        v-if="isUploadingImage"
        class="mt-2 text-sm text-gray-500"
      >
        Uploading...
      </div>
    </div>

    <!-- Right Column: Form Fields -->
    <div class="md:col-span-9">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Name -->
        <div class="p-2">
          <label
            for="name"
            class="block text-gray-700 mb-2 font-medium"
          >Name</label>
          <InputText
            id="name"
            :model-value="contact.name"
            :disabled="!isEditing"
            class="w-full"
            @update:model-value="updateField('name', $event)"
          />
        </div>

        <!-- Description -->
        <div class="p-2 md:col-span-2">
          <label
            for="description"
            class="block text-gray-700 mb-2 font-medium"
          >Description</label>
          <Textarea
            id="description"
            :model-value="contact.description"
            :disabled="!isEditing"
            class="w-full"
            rows="3"
            @update:model-value="updateField('description', $event)"
          />
        </div>

        <!-- Job Title -->
        <div class="p-2">
          <label
            for="position"
            class="block text-gray-700 mb-2 font-medium"
          >Job Title</label>
          <InputText
            id="position"
            :model-value="contact.position"
            :disabled="!isEditing"
            class="w-full"
            @update:model-value="updateField('position', $event)"
          />
        </div>

        <!-- Phone Number -->
        <div class="p-2">
          <label
            for="phone"
            class="block text-gray-700 mb-2 font-medium"
          >Phone Number</label>
          <InputText
            id="phone"
            :model-value="contact.phone"
            :disabled="!isEditing"
            class="w-full"
            @update:model-value="updateField('phone', $event)"
          />
        </div>

        <!-- Email -->
        <div class="p-2">
          <label
            for="email"
            class="block text-gray-700 mb-2 font-medium"
          >Email</label>
          <InputText
            id="email"
            :model-value="contact.email"
            :disabled="!isEditing"
            class="w-full"
            @update:model-value="updateField('email', $event)"
          />
        </div>

        <!-- Company -->
        <div class="p-2">
          <label
            for="company_name"
            class="block text-gray-700 mb-2 font-medium"
          >Company</label>
          <InputText
            id="company_name"
            :model-value="contact.company_name"
            :disabled="!isEditing"
            class="w-full"
            @update:model-value="updateField('company_name', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject } from 'vue';
import { useToast } from 'primevue/usetoast';
import { uploadContactImage } from '@/services/contactService';
import ProfileImage from '@/components/shared/ProfileImage.vue';

const props = defineProps({
    contact: {
        type: Object,
        required: true
    },
    isEditing: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:contact']);

const toast = useToast();
const isUploadingImage = ref(false);

// Try to get image upload handler from parent (for add mode)
const handleImageUpload = inject('handleImageUpload', null);

const updateField = (field, value) => {
    const updatedContact = { ...props.contact, [field]: value };
    emit('update:contact', updatedContact);
};

const onImageUpload = async (event) => {
    const file = event.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
        toast.add({
            severity: 'error',
            summary: 'Invalid File Type',
            detail: 'Please upload a JPEG, PNG, GIF, or WebP image',
            life: 5000
        });
        return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        toast.add({
            severity: 'error',
            summary: 'File Too Large',
            detail: 'Image must be less than 5MB',
            life: 5000
        });
        return;
    }
    
    // If contact doesn't have an ID (add mode), use parent handler
    if (!props.contact.id && handleImageUpload) {
        handleImageUpload(file);
        return;
    }
    
    // If contact has ID (edit mode), upload immediately
    if (props.contact.id) {
        isUploadingImage.value = true;
        
        try {
            // Upload image
            const response = await uploadContactImage(props.contact.id, file);
            
            // Update contact with new image URL
            const updatedContact = {
                ...props.contact,
                image: response.data?.image_url || response.data?.image || props.contact.image,
                image_url: response.data?.image_url || response.data?.image || props.contact.image_url
            };
            emit('update:contact', updatedContact);
            
            toast.add({
                severity: 'success',
                summary: 'Image Uploaded',
                detail: 'Profile image updated successfully!',
                life: 5000
            });
        } catch (error) {
            console.error('Error uploading image:', error);
            const errorMessage = error.response?.data?.message || 'Failed to upload image';
            toast.add({
                severity: 'error',
                summary: 'Upload Failed',
                detail: errorMessage,
                life: 7000
            });
        } finally {
            isUploadingImage.value = false;
        }
    }
};
</script> 